import {
  TriggerDatasetType,
  type TriggerInputDataset,
} from '@/hooks/api/datasets/trigger/useTriggerManualRun.ts';
import type { ManualTriggerInput } from '@/types/automationSteps.ts';
import type { TriggerDataset } from '@/types/datasets.ts';

export const normalizeTriggerId = (triggerId?: string | null) => triggerId ?? '';

export const buildTriggerDatasetMap = (inputDatasets: TriggerDataset[]) =>
  new Map(inputDatasets.map((dataset) => [normalizeTriggerId(dataset.trigger_id), dataset] as const));

export type TriggerDatasetCompatibility = {
  matchedInputs: TriggerDataset[];
  matchedTriggerCount: number;
  missingRequiredTriggers: ManualTriggerInput[];
  missingOptionalTriggers: ManualTriggerInput[];
  extraInputCount: number;
  matchType: 'exact' | 'partial' | 'none';
};

export const analyzeTriggerDatasetCompatibility = ({
  triggers,
  inputDatasets,
}: {
  triggers: ManualTriggerInput[];
  inputDatasets: TriggerDataset[];
}): TriggerDatasetCompatibility => {
  const datasetsByTriggerId = buildTriggerDatasetMap(inputDatasets);

  const matchedInputs = triggers
    .map((trigger) => datasetsByTriggerId.get(normalizeTriggerId(trigger.output_id)))
    .filter((dataset): dataset is TriggerDataset => dataset !== undefined);

  const missingTriggers = triggers.filter(
    (trigger) => !datasetsByTriggerId.has(normalizeTriggerId(trigger.output_id)),
  );
  const missingRequiredTriggers = missingTriggers.filter((trigger) => trigger.is_required);
  const missingOptionalTriggers = missingTriggers.filter((trigger) => !trigger.is_required);
  const extraInputCount = inputDatasets.length - matchedInputs.length;
  const hasNoCurrentMatches = matchedInputs.length === 0;
  const canTreatNoInputRunAsMatch =
    extraInputCount === 0 && (triggers.length === 0 || missingRequiredTriggers.length === 0);

  let matchType: TriggerDatasetCompatibility['matchType'] = 'partial';
  if (hasNoCurrentMatches && !canTreatNoInputRunAsMatch) {
    matchType = 'none';
  } else if (missingRequiredTriggers.length === 0 && extraInputCount === 0) {
    matchType = 'exact';
  }

  return {
    matchedInputs,
    matchedTriggerCount: matchedInputs.length,
    missingRequiredTriggers,
    missingOptionalTriggers,
    extraInputCount,
    matchType,
  };
};

export const applyRunInputsToTriggerDatasets = ({
  currentTriggerDatasets,
  triggers,
  inputDatasets,
}: {
  currentTriggerDatasets: TriggerInputDataset[];
  triggers: ManualTriggerInput[];
  inputDatasets: TriggerDataset[];
}): TriggerInputDataset[] => {
  const datasetsByTriggerId = buildTriggerDatasetMap(inputDatasets);

  return currentTriggerDatasets.map((dataset, index) => {
    const trigger = triggers[index];
    if (!trigger) {
      return dataset;
    }

    const matchedDataset = datasetsByTriggerId.get(normalizeTriggerId(trigger.output_id));
    if (!matchedDataset) {
      return dataset;
    }

    return {
      trigger_id: trigger.output_id || '',
      dataset_id: matchedDataset.dataset.id,
      type: TriggerDatasetType.Existing,
    };
  });
};
